/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author: DQTuan
 * @crated at: 26/12/2024
 *
 **/
import { useLocation } from "react-router-dom";

import { MeetingStatus, MeetingType } from "@/frameworks/core/hooks/useScene/type";
import { SPACE_SCENE_TYPE } from "@/frameworks/core/commons";
import { useMeeting } from "@/frameworks/core/API";
import { Configure, Properties } from "@/frameworks/libraries";
import { serviceBaseSceneProcess } from "@/frameworks/core/unity";
import { usePersistActions } from "../../persistence";

/**
 * Use scene hook
 */
const useScene = () => {
	// Declare hook & variable
	const meetingAPI = useMeeting();
	const location = useLocation();
	const { users } = usePersistActions();

	/**
	 * Get scene from conference
	 * @param conference
	 * @returns SceneModel | null
	 */
	const getSceneFromConference = (
		conference: SwaggerMeetingAPIModels.Conference
	): SwaggerModels.UserSpaceResponseVO | SwaggerMeetingAPIModels.Conference | null => {
		if (conference) {
			return {
				id: conference.id,
				title: conference?.title,
				accessCode: conference.accessCode,
				roomNumber: conference?.roomNumber,
				chatRoomId: conference?.chatRoomId,
				status: conference?.status,
				privateMeeting: conference?.privateMeeting,
				participants: conference?.participants ?? [],
				spaceScene: {
					path: conference.spaceScene?.path,
					version: conference.spaceScene?.version,
					sceneLevel: conference.spaceScene?.sceneLevel,
					sceneType: {
						type: SPACE_SCENE_TYPE.MEETING_ROOM,
						typeNumber: conference.spaceScene?.sceneType?.typeNumber
					}
				}
			};
		}
		return null;
	};

	/**
	 * Get scene from invited link.
	 * This function will use when user click on the invited link
	 * @param meetingType
	 * @param meetingID
	 */
	const getSceneFromInvitedLink = async (
		meetingType: MeetingType,
		meetingID: string
	): Promise<SwaggerModels.UserSpaceResponseVO | SwaggerMeetingAPIModels.Conference | null> => {
		try {
			// Handle when the meeting type is conference
			if (meetingType === MeetingType.CONFERENCE) {
				// Get conference detail
				const conference: SwaggerMeetingAPIModels.Conference = await meetingAPI.conference.detail({
					conferenceId: meetingID
				});
				if (conference) {
					return getSceneFromConference(conference);
				}
			} else if (meetingType === MeetingType.SCHEDULE) {
				// Handle meeting type is schedule then start to get the meeting detail
				const meetingDetail = await meetingAPI.meeting.detail({
					scheduleId: meetingID
				});

				// If the meeting detail is not null and the status is MeetingStatus.OPEN
				const EMPTY_STRING = "";
				if (meetingDetail && meetingDetail?.status === MeetingStatus.OPEN) {
					const conference: SwaggerMeetingAPIModels.Conference =
						await meetingAPI.conference.partipateFormSchedule({
							scheduleId: meetingDetail?.scheduleId || EMPTY_STRING,
							scheduleStartTime: meetingDetail?.startTime || 0,
							spaceThemeId: meetingDetail?.spaceThemeId || EMPTY_STRING,
							deviceKey: Properties.browserTabID
						});
					if (conference) {
						return getSceneFromConference(conference);
					}
				}
			}
		} catch (error) {
			return null;
		}
		return null;
	};

	/**
	 * Get scene data from invited link or current conference
	 */
	const getScene = async (): Promise<SwaggerModels.UserSpaceResponseVO | SwaggerMeetingAPIModels.Conference> => {
		// If the use join a meeting as guest user
		if (location.state?.params?.isMeetingGuest) {
			const meetingType = location.state?.params?.meetingType;
			const meetingId = location.state?.id;
			const scene = await getSceneFromInvitedLink(meetingType, meetingId);
			if (scene) {
				return scene;
			}
		}
		const currentMeeting = Configure.currentConference;
		// If the current meeting is not null & the conference ID is not null
		if (currentMeeting && currentMeeting.conferenceId) {
			try {
				const { conferenceId, accessCode, path, version, sceneLevel, sceneType } = currentMeeting;
				// Start to get meeting information
				const resp = await meetingAPI.conference.detail({ conferenceId });
				if (resp && resp.status === MeetingStatus.OPEN) {
					return {
						id: conferenceId,
						accessCode: accessCode,
						spaceScene: {
							path: path as string,
							version: version as string,
							sceneLevel: sceneLevel,
							sceneType: {
								type: SPACE_SCENE_TYPE.MEETING_ROOM,
								typeNumber: sceneType
							}
						}
					} as SwaggerMeetingAPIModels.Conference;
				}
			} finally {
				Configure.currentConference = null;
				Configure.openingScheduleConference = null;
			}
		}
		const me = users.get_user(Configure.userID);
		return serviceBaseSceneProcess({
			lastSpace: me?.lastSpace,
			entrySpace: me?.entrySpace,
			myRoomID: me!.myRoomId!
		});
	};

	return {
		getScene
	};
};

export default useScene;
